// tslint:disable:max-line-length
/**
 * Validation Error Interface
 */
import {STRING_MAP} from "../../types";

export interface ITCIValidationError {
    /**
     * Error Code
     * @private
     */
    errorCode: string;
    /**
     * Error Message
     * @private
     */
    errorMsg: string;
    /**
     * Error Context
     * @private
     */
    context?: STRING_MAP<string>;
    /**
     * Is Warning
     */
    warning: boolean;

    /**
     * Returns Error Code
     * @return {string}
     */
    getErrorCode(): string;

    /**
     * Returns Error Message
     * @return {string}
     */
    getErrorMessage(): string;

    /**
     * Returns true if this error is a warning
     */
    isWarning(): boolean;

    /**
     * Set the warning flag value
     * @param val
     */
    setWarning(val: boolean): void;

    /**
     * Returns a string representation of the Error
     * @return {string}
     */
    toString(): string;

    /**
     * Set Context Map for the Error i.e Flow -> Activity -> Property
     * @private Internal Use
     * @param {STRING_MAP<string>} context
     */
    setContext(context: STRING_MAP<string>);
}

/**
 * Validation Error Implementation
 */
export class ValidationError implements ITCIValidationError {
    /**
     * Static Constructor Create a new validation error instance
     * @param {string} errorCode
     * @param {string} errorMsg
     * @return {IValidationError}
     */
    public static newError(errorCode: string, errorMsg: string): ITCIValidationError {
        return new ValidationError(errorCode, errorMsg, false);
    }

    /**
     * Static Constructor Create a new validation error instance
     * @param {string} errorCode
     * @param {string} errorMsg
     * @return {IValidationError}
     */
    public static newWarning(errorCode: string, errorMsg: string): ITCIValidationError {
        return new ValidationError(errorCode, errorMsg, true);
    }

    /**
     * Constructor - Create a new validation error instance
     * @constructor
     * @param {string} errorCode
     * @param {string} errorMsg
     * @param {boolean} warning
     * @param {STRING_MAP<string>} context
     */
    constructor(public errorCode: string, public errorMsg: string, public warning: boolean, public context?: STRING_MAP<string>) {
        if (!context) {
            context = new Map<string, string>();
        }
    }

    /**
     * Returns Error Code
     * @return {string}
     */
    public getErrorCode(): string {
        return this.errorCode;
    }

    /**
     * Returns Error Message
     * @return {string}
     */
    public getErrorMessage(): string {
        return this.errorMsg;
    }

    /**
     * Returns true if this is a warning
     * @return {boolean}
     */
    public isWarning(): boolean {
        return this.warning;
    }

    /**
     * Set the warning flag value
     * @param val
     */
    setWarning(val: boolean): void {
        this.warning = val;
    }

    /**
     * Returns a string representation of the error
     * @return {string}
     */
    public toString(): string {
        return this.errorCode + ":" + this.errorMsg;
    }

    /**
     * Set the error context
     * @private Internal Use only
     * @param {STRING_MAP<string>} context
     */
    public setContext(context: STRING_MAP<string>) {
        this.context = context;
    }
}

/**
 * Field Validation State Enumeration
 */
export class TCIFieldValidationState {
    /**
     * The field is read only based on the bit flag
     * @type {number}
     */
    public static TCI_FIELD_READONLY = 0x2;
    /**
     *  The field is visible based on the bit flag
     * @type {number}
     */
    public static TCI_FIELD_VISIBLE = 0x4;
    /**
     * The field is valid based on the bit flag
     * @type {number}
     */
    public static TCI_FIELD_VALID = 0x8;
    /**
     * The field is exportable based on the bit flag
     * @type {number}
     */
    public static TCI_FIELD_EXPORTABLE = 0x10;
    /**
     * The field is encryptable based on the bit flag
     * @type {number}
     */
    public static TCI_FIELD_ENCRYPTABLE = 0x20;

}

/**
 * Action validation state enumeration
 */
export class TCIActionState {
    /**
     * The action result is OK
     */
    public static TCI_ACTION_OK = 0x0002;
    /**
     * The action result has warnings
     */
    public static TCI_ACTION_WARNING = 0x0004;
    /**
     * The action result has errors
     */
    public static TCI_ACTION_ERROR = 0x0008;

}

export class TCIValidationResultType {
    public static VALIDATION_TYPE_FIELD = 0;
    public static VALIDATION_TYPE_ACTION = 1;
    public static VALIDATION_TYPE_TENANT = 2;
    public static VALIDATION_TYPE_USER_ROLES = 3;
}

/**
 * Validation Result interface
 */
export interface ITCIValidationResult {
    /**
     * Validation Result Type
     */
    type: TCIValidationResultType;
    /**
     * Validation State
     * @private
     */
    state: TCIFieldValidationState | TCIActionState | number;
    /**
     * Validation Errors
     * @private
     */
    errors: ITCIValidationError[];

    /**
     * Return the validation result type
     */
    getType(): TCIValidationResultType;

    /**
     * Adds Validation error
     * @param {string} errorCode
     * @param {string} errorMessage
     * @return {ITCIFieldValidationResult}
     */
    setError(errorCode: string, errorMessage: string): ITCIValidationResult;

    /**
     * Returns Validation Errors
     * @return {IValidationError[]}
     */
    getErrors(): ITCIValidationError[];
}

/**
 * Action Validation Result interface
 */
export interface ITCIActionValidationResult extends ITCIValidationResult {
    /**
     * Returns true if the action result state {EnumActionValidationState} is {EnumActionValidationState.TCI_ACTION_OK}
     */
    isOK(): boolean;

    /**
     * Returns true if the action result state {EnumActionValidationState} is {EnumActionValidationState.TCI_ACTION_WARNING}
     */
    isWarning(): boolean;

    /**
     * Returns true if the action result state {EnumActionValidationState} is {EnumActionValidationState.TCI_ACTION_ERROR}
     */
    isError(): boolean;

    /**
     * Set validation state
     * @param state
     */
    setState(state: TCIActionState): ITCIActionValidationResult;


}

/**
 * class TCIActionValidationResult
 */
export class TCIActionValidationResult implements ITCIActionValidationResult {

    // tslint:disable-next-line:no-bitwise
    state: TCIFieldValidationState | TCIActionState | number;
    errors: ITCIValidationError[] = [];
    type: TCIValidationResultType;

    /**
     * Static constructor
     * @param {IValidationError | IValidationError[]} errors
     * @return {ITCIActionValidationResult}
     */
    static newValidationResult(errors?: ITCIValidationError | ITCIValidationError[]): ITCIActionValidationResult {
        return new TCIActionValidationResult(errors);
    }

    /**
     * @constructor
     * @param {IValidationError | IValidationError[]} errors
     */
    constructor(errors?: ITCIValidationError | ITCIValidationError[]) {
        this.type = TCIValidationResultType.VALIDATION_TYPE_FIELD;
        this.errors = [];
        if (errors) {
            if (!Array.isArray(errors)) {
                this.errors.push(errors);
            } else {
                this.errors = this.errors.concat(errors);
            }
        }
    }

    getType(): TCIValidationResultType {
        return this.type;
    }

    // tslint:disable:no-bitwise
    /**
     * Returns true if the action result state {EnumActionValidationState} is {EnumActionValidationState.TCI_ACTION_OK}
     */
    public isOK(): boolean {
        return this.state === TCIActionState.TCI_ACTION_OK;
    }

    /**
     * Returns true if the action result state {EnumActionValidationState} is {EnumActionValidationState.TCI_ACTION_WARNING}
     */
    public isWarning(): boolean {
        return this.state === TCIActionState.TCI_ACTION_WARNING;
    }

    /**
     * Returns true if the action result state {EnumActionValidationState} is {EnumActionValidationState.TCI_ACTION_ERROR}
     */
    public isError(): boolean {
        return this.state === TCIActionState.TCI_ACTION_ERROR;
    }

    /**
     * Set validation state
     * @param state
     */
    public setState(state: TCIActionState): ITCIActionValidationResult {
        this.state = state;
        return this;
    }

    /**
     * Adds Validation error
     * @param {string} errorCode
     * @param {string} errorMessage
     * @return {ITCIFieldValidationResult}
     */
    public setError(errorCode: string, errorMessage: string): ITCIValidationResult {
        this.errors.push(ValidationError.newError(errorCode, errorMessage));
        return this;
    }

    /**
     * Returns Validation Errors
     * @return {IValidationError[]}
     */
    getErrors(): ITCIValidationError[] {
        return this.errors.filter((val) =>
            !val.isWarning()
        );
    }

    /**
     * Returns Validation Errors
     * @return {IValidationError[]}
     */
    getWarnings(): ITCIValidationError[] {
        return this.errors.filter((val) =>
            val.isWarning()
        );
    }

    /**
     * Adds Validation Warning
     * @param {string} errorCode
     * @param {string} errorMessage
     * @return {ITCIFieldValidationResult}
     */
    public setWarning(errorCode: string, errorMessage: string): ITCIValidationResult {
        this.errors.push(ValidationError.newWarning(errorCode, errorMessage));
        return this;
    }
}

/**
 * Field Validation Result interface
 */
export interface ITCIFieldValidationResult extends ITCIValidationResult {


    /**
     * Returns the Validation Read Only state
     * @return {boolean}
     */
    isReadOnly(): boolean;

    /**
     * Returns the Validation Visible state
     * @return {boolean}
     */
    isVisible(): boolean;

    /**
     * Returns the Validation validity state
     * @return {boolean}
     */
    isValid(): boolean;

    /**
     * Returns the Validation exportable state
     * @return {boolean}
     */
    isExportable(): boolean;

    /**
     * Returns the Validation encryptable state
     * @return {boolean}
     */
    isEncryptable(): boolean;


    /**
     * Sets the Validation Read Only state
     * @param {boolean} res
     * @return {ITCIFieldValidationResult}
     */
    setReadOnly(res: boolean): ITCIFieldValidationResult;

    /**
     * Sets the Validation Visible state
     * @param {boolean} res
     * @return {ITCIFieldValidationResult}
     */
    setVisible(res: boolean): ITCIFieldValidationResult;

    /**
     * Sets the Validation valid state
     * @param {boolean} res
     * @return {ITCIFieldValidationResult}
     */
    setValid(res: boolean): ITCIFieldValidationResult;

    /**
     * Sets the Validation exportable state
     * @param {boolean} res
     * @return {ITCIFieldValidationResult}
     */
    setExportable(res: boolean): ITCIFieldValidationResult;

    /**
     * Sets the Validation encryptable state
     * @param {boolean} res
     * @return {ITCIFieldValidationResult}
     */
    setEncryptable(res: boolean): ITCIFieldValidationResult;


}

/**
 * Validation Result Implementation
 */
export class TCIFieldValidationResult implements ITCIFieldValidationResult {

    // tslint:disable-next-line:no-bitwise
    state: number = TCIFieldValidationState.TCI_FIELD_VALID | TCIFieldValidationState.TCI_FIELD_VISIBLE;
    errors: ITCIValidationError[] = [];
    type: TCIValidationResultType;

    /**
     * Static constructor
     * @param {IValidationError | IValidationError[]} errors
     * @return {ITCIFieldValidationResult}
     */
    static newValidationResult(errors?: ITCIValidationError | ITCIValidationError[]): ITCIFieldValidationResult {
        return new TCIFieldValidationResult(errors);
    }

    /**
     * @constructor
     * @param {IValidationError | IValidationError[]} errors
     */
    constructor(errors?: ITCIValidationError | ITCIValidationError[]) {
        this.type = TCIValidationResultType.VALIDATION_TYPE_FIELD;
        this.errors = [];
        if (errors) {
            this.setValid(false);
            if (!Array.isArray(errors)) {
                this.errors.push(errors);
            } else {
                this.errors = this.errors.concat(errors);
            }
        }
    }

    getType(): TCIValidationResultType {
        return this.type;
    }

    // tslint:disable:no-bitwise

    isReadOnly(): boolean {
        return ((this.state & TCIFieldValidationState.TCI_FIELD_READONLY) === TCIFieldValidationState.TCI_FIELD_READONLY);
    }

    isVisible(): boolean {
        return ((this.state & TCIFieldValidationState.TCI_FIELD_VISIBLE) === TCIFieldValidationState.TCI_FIELD_VISIBLE);
    }

    isValid(): boolean {
        return ((this.state & TCIFieldValidationState.TCI_FIELD_VALID) === TCIFieldValidationState.TCI_FIELD_VALID);
    }

    isExportable(): boolean {
        return ((this.state & TCIFieldValidationState.TCI_FIELD_EXPORTABLE) === TCIFieldValidationState.TCI_FIELD_EXPORTABLE);
    }

    isEncryptable(): boolean {
        return ((this.state & TCIFieldValidationState.TCI_FIELD_ENCRYPTABLE) === TCIFieldValidationState.TCI_FIELD_ENCRYPTABLE);
    }


    setReadOnly(res: boolean): ITCIFieldValidationResult {
        this.state = res ? this.state | TCIFieldValidationState.TCI_FIELD_READONLY : this.state & ~TCIFieldValidationState.TCI_FIELD_READONLY;
        return this;
    }

    setVisible(res: boolean): ITCIFieldValidationResult {
        this.state = res ? this.state | TCIFieldValidationState.TCI_FIELD_VISIBLE : this.state & ~TCIFieldValidationState.TCI_FIELD_VISIBLE;
        return this;
    }

    setValid(res: boolean): ITCIFieldValidationResult {
        this.state = res ? this.state | TCIFieldValidationState.TCI_FIELD_VALID : this.state & ~TCIFieldValidationState.TCI_FIELD_VALID;
        return this;
    }

    setExportable(res: boolean): ITCIFieldValidationResult {
        this.state = res ? this.state | TCIFieldValidationState.TCI_FIELD_EXPORTABLE : this.state & ~TCIFieldValidationState.TCI_FIELD_EXPORTABLE;
        return this;
    }

    setEncryptable(res: boolean): ITCIFieldValidationResult {
        this.state = res ? this.state | TCIFieldValidationState.TCI_FIELD_ENCRYPTABLE : this.state & ~TCIFieldValidationState.TCI_FIELD_ENCRYPTABLE;
        return this;
    }


    setError(errorCode: string, errorMessage: string): ITCIFieldValidationResult {
        this.errors.push(ValidationError.newError(errorCode, errorMessage));
        return this;
    }

    getErrors(): ITCIValidationError[] {
        return this.errors;
    }
}

/**
 * Tenant service Validation Result Implementation
 */
export class TCITenantValidationResult extends TCIActionValidationResult implements ITCIValidationResult {
    /**
     * @constructor
     * @param {IValidationError | IValidationError[]} errors
     */
    constructor(data?: ITCIValidationResult) {
        super(data ? data.errors : []);
        this.type = TCIValidationResultType.VALIDATION_TYPE_TENANT;
        this.state = data ? data.state : TCIActionState.TCI_ACTION_OK;
    }
}
